import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import { FormDataArchive, IUseQuery, MutationResult, QueryHookParams } from "../models";
import { useAuth } from "../contexts";

export interface IUseFormDataArchiveQuery extends Pick<IUseQuery<FormDataArchive>, "listQuery" | "detailQuery"> {
    uploadMutation: MutationResult<FormDataArchive, unknown, FormData>;
    addFileMutation: MutationResult<FormDataArchive, unknown, FormData>;
}

interface UseFormDataArchiveQueryParams extends QueryHookParams {
    formDataId?: number;
}

export const useFormDataArchiveQuery = (params?: UseFormDataArchiveQueryParams): IUseFormDataArchiveQuery => {
    const formDataArchiveId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const formDataId = isNaN(Number(params?.formDataId)) ? 0 : Number(params?.formDataId);
    const { get, post, put } = useAxios();
    const { isAuthorized } = useAuth();
    const url = Endpoints.FormDataArchives;

    const listQuery = useGenericQuery({
        queryFn: (config) => get<FormDataArchive[]>({ url: `${url}?formDataId=${formDataId}`, config }),
        queryKey: queryKeys.formDataArchives.list({ formDataId }),
        placeholderData: [],
        enabled: false, // isAuthorized && !!formDataId, TODO: not sure if we have a use for this
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<FormDataArchive>({ url: `${url}/${formDataArchiveId}`, config }),
        queryKey: queryKeys.formDataArchives.detail(formDataArchiveId),
        placeholderData: undefined,
        enabled: isAuthorized && !!formDataArchiveId,
    });

    const uploadMutation = useGenericMutation({
        mutationFn: (data: FormData) => post<FormData, FormDataArchive>({ url: `${url}/upload/${formDataId}`, data }),
        queryKey: queryKeys.formData.detail(formDataId),
        keysToInvalidate: [],
        successToast: "Form archived successfully",
    });

    const addFileMutation = useGenericMutation({
        mutationFn: (data: FormData) => put<FormData, FormDataArchive>({ url: `${url}/${formDataArchiveId}`, data }),
        queryKey: queryKeys.formDataArchives.detail(formDataArchiveId),
        keysToInvalidate: [queryKeys.formData.detail(formDataId)],
        successToast: "Archived form saved as a PDF",
    });

    return useMemo<IUseFormDataArchiveQuery>(
        () => ({
            listQuery,
            detailQuery,
            uploadMutation,
            addFileMutation,
        }),
        [addFileMutation, detailQuery, listQuery, uploadMutation]
    );
};
